import { useFormik } from 'formik';
import { Parser } from 'html-to-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';

import { CancelReservationModel } from '../../../models';
import {
  cancelReservation,
  getReservationByCancelCode
} from '../../../services';
import {
  CalendarDbIcon,
  ClockDbIcon,
  GlobeDbIcon,
  GroupDbIcon,
  LocationDbIcon
} from '../../components';

export function CancelRegistrationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { cancelCode } = useParams();

  const [reservation, setReservation] = useState<CancelReservationModel>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(undefined);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [disableDoubleClick, setDisableDoubleClick] = useState(false);

  const handleClickDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleClickDialogClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    setLoading(true);
    getReservationByCancelCode(cancelCode ?? '')
      .then((response) => {
        setReservation(response);
        if (response.numberOfGuests === undefined) {
          navigate('/registration-not-found');
        }
      })
      .catch((error) => setError(error))
      .finally(() => {
        setLoading(false);
      });
  }, [cancelCode, navigate]);

  const getEventyTypeWithLectureName = () => {
    const eventTypeName =
      i18n.language === 'en'
        ? reservation?.eventTypeInEnglish
        : reservation?.eventTypeInGerman;
    const lectureName =
      i18n.language === 'en'
        ? reservation?.lectureNameInEnglish
        : reservation?.lectureNameInGerman;
    return lectureName ? eventTypeName + ' - ' + lectureName : eventTypeName;
  };

  const securityWordRegexp = /^([cC][aA][nN][cC][eE][lL])+$/;
  const cancelReservationValidationSchema = yup.object().shape({
    securityWord: yup
      .string()
      .matches(
        securityWordRegexp,
        t('eventRegistration.validationText.invalidSecurityWord')
      )
      .required(t('eventRegistration.validationText.required') ?? undefined)
  });

  const formik = useFormik({
    initialValues: {
      securityWord: ''
    },
    enableReinitialize: true,
    validationSchema: cancelReservationValidationSchema,
    onSubmit: () => {
      setDisableDoubleClick(true);
      setLoading(true);
      handleClickDialogClose();
      cancelReservation(cancelCode ?? '')
        .then(() => {
          navigate('completed');
        })
        .catch((error) => setError(error))
        .finally(() => {
          setLoading(false);
          setDisableDoubleClick(false);
        });
    }
  });

  return (
    <Stack spacing={2} sx={{ p: 3, height: '100%' }}>
      <Stack sx={{ overflow: 'auto' }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Typography
              variant="h3"
              component="h3"
              data-testid="eventName"
              sx={{ paddingTop: '1.5rem' }}
            >
              {getEventyTypeWithLectureName()}
            </Typography>
            <Typography
              variant="h5"
              component="h5"
              data-testid="eventTime"
              sx={(theme) => ({
                color: theme.palette.primary.main
              })}
            >
              {t('events.from') +
                ' ' +
                (reservation?.startTime ?? '') +
                ' - ' +
                (reservation?.endTime ?? '')}
            </Typography>
            {error && (
              <Alert severity="error" data-testid="errorMessage">
                {error?.response?.data?.message ??
                  t('common.unknownErrorOccurred')}
              </Alert>
            )}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="flex-start"
              alignItems="flex-start"
              spacing={5}
              sx={{ paddingTop: '1.5rem', width: '100%' }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/visitor-center_keyvisual-1280x776.jpg`}
                sx={{ width: { xs: '100%', sm: '40%' }, height: '100%' }}
              />
              <Box
                sx={(theme) => ({
                  backgroundColor: theme.palette.background.paper,
                  width: { xs: '100%', sm: '60%' },
                  minHeight: { xs: 370, sm: 280 }
                })}
              >
                <Stack
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  spacing={2}
                  sx={{ p: 3 }}
                >
                  <Typography
                    variant="h6"
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontWeight: 'bold'
                    })}
                  >
                    {t('eventRegistration.bookingDetails')}
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ paddingTop: 1 }}
                  >
                    <Typography variant="body2">
                      <CalendarDbIcon />
                    </Typography>
                    <Typography
                      variant="body2"
                      data-testid="eventStartTime"
                      sx={{ paddingTop: '0.15rem' }}
                    >
                      {reservation?.startDate ?? ''}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ paddingTop: 1 }}
                  >
                    <Typography variant="body2">
                      <ClockDbIcon />
                    </Typography>
                    <Typography
                      variant="body2"
                      data-testid="eventStartEndTime"
                      sx={{ paddingTop: '0.15rem' }}
                    >
                      {t('events.from') +
                        ' ' +
                        (reservation?.startTime ?? '') +
                        ' ' +
                        t('events.to') +
                        ' ' +
                        (reservation?.endTime ?? '') +
                        ' ' +
                        t('common.cet')}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ paddingTop: 1 }}
                  >
                    <Typography variant="body2">
                      <LocationDbIcon />
                    </Typography>
                    <Typography
                      variant="body2"
                      data-testid="eventLocation"
                      sx={{ paddingTop: '0.15rem' }}
                    >
                      {(reservation?.street ?? '') +
                        ' ' +
                        (reservation?.houseNumber ?? '') +
                        ', ' +
                        (reservation?.postCode ?? '') +
                        ' ' +
                        (reservation?.city ?? '')}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={2}
                    sx={{ paddingTop: 1 }}
                  >
                    <Typography variant="body2">
                      <GroupDbIcon />
                    </Typography>
                    <Typography
                      variant="body2"
                      data-testid="eventAvailability"
                      sx={{ paddingTop: '0.15rem' }}
                    >
                      {reservation?.numberOfGuests ?? 0}
                    </Typography>
                  </Stack>
                  {reservation?.languageInEnglish && (
                    <Stack
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      spacing={2}
                      sx={{ paddingTop: 1 }}
                    >
                      <Typography variant="body2">
                        <GlobeDbIcon />
                      </Typography>
                      <Typography
                        variant="body2"
                        data-testid="eventLanguage"
                        sx={{ paddingTop: '0.15rem' }}
                      >
                        {i18n.language === 'en'
                          ? reservation?.languageInEnglish
                          : reservation?.languageInGerman}
                      </Typography>
                    </Stack>
                  )}
                  {(reservation?.numberOfGuests ?? 0) > 0 && (
                    <Stack
                      direction="row"
                      justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                      alignItems="center"
                      spacing={2}
                      sx={{ padding: 3, width: '100%' }}
                    >
                      <Button
                        variant="contained"
                        data-testid="registerButton"
                        sx={(theme) => ({
                          display: 'inline',
                          width: { xs: '100%', sm: '30%' },
                          backgroundColor: theme.palette.primary.main
                        })}
                        onClick={handleClickDialogOpen}
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight="bold"
                          sx={(theme) => ({
                            color: theme.palette.info.main
                          })}
                        >
                          {t('eventRegistration.cancelBooking') + ' '}
                        </Typography>
                      </Button>

                      <Dialog
                        open={openDialog}
                        onClose={handleClickDialogClose}
                      >
                        <form onSubmit={formik.handleSubmit}>
                          <DialogTitle>
                            <Stack
                              spacing={{ xs: 3, sm: 8 }}
                              direction="column"
                              justifyContent="space-evenly"
                              alignItems="flex-start"
                            >
                              <Typography
                                variant="h5"
                                component="h5"
                                data-testid="cancelDialogTitle"
                                sx={(theme) => ({
                                  color: theme.palette.primary.main,
                                  fontWeight: 'bold'
                                })}
                              >
                                {t('eventRegistration.cancelBooking')}
                              </Typography>
                            </Stack>
                          </DialogTitle>
                          <DialogContent>
                            <Stack
                              spacing={{ xs: 3, sm: 8 }}
                              direction="column"
                              justifyContent="space-evenly"
                              alignItems="flex-start"
                            >
                              <DialogContentText>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  data-testid="cancelDialogText"
                                >
                                  {t(
                                    'eventRegistration.cancelBookingDialogText'
                                  )}
                                </Typography>
                              </DialogContentText>
                            </Stack>

                            <Stack
                              spacing={{ xs: 3, sm: 8 }}
                              direction="column"
                              justifyContent="space-evenly"
                              alignItems="flex-start"
                            >
                              <TextField
                                label={t('eventRegistration.securityWord')}
                                placeholder={
                                  t('eventRegistration.securityWord') ?? ''
                                }
                                margin="dense"
                                id="securityWord"
                                name="securityWord"
                                value={formik.values.securityWord}
                                onChange={formik.handleChange}
                                error={Boolean(
                                  formik.touched.securityWord &&
                                    formik.errors.securityWord
                                )}
                                helperText={
                                  formik.touched.securityWord &&
                                  formik.errors.securityWord
                                }
                                required
                                fullWidth
                                variant="standard"
                                data-testid="cancelDialogTextField"
                              />
                            </Stack>
                          </DialogContent>
                          <DialogActions>
                            <Button
                              color="primary"
                              variant="contained"
                              onClick={handleClickDialogClose}
                              data-testid="cancelDialogCloseBtn"
                            >
                              <Typography variant="h5">
                                {t('eventRegistration.closeDialog')}
                              </Typography>
                            </Button>
                            <Button
                              type="submit"
                              color="primary"
                              variant="contained"
                              disabled={
                                loading ||
                                disableDoubleClick ||
                                !formik.isValid ||
                                formik.values.securityWord?.length < 1
                              }
                              data-testid="cancelDialogSubmitBtn"
                            >
                              <Typography variant="h5">
                                {t('eventRegistration.cancelBooking')}
                              </Typography>
                            </Button>
                          </DialogActions>
                        </form>
                      </Dialog>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>

            {reservation?.lectureTypeDescriptionInEnglish && (
              <Stack sx={{ paddingTop: 8 }}>
                <Box data-testid="lectureDescription">
                  {i18n.language === 'en'
                    ? Parser().parse(
                        reservation.lectureTypeDescriptionInEnglish ?? ''
                      )
                    : Parser().parse(
                        reservation.lectureTypeDescriptionInGerman ?? ''
                      )}
                </Box>
              </Stack>
            )}

            <Stack>
              <Box data-testid="eventDescription">
                {i18n.language === 'en'
                  ? Parser().parse(
                      reservation?.eventTypeDescriptionInEnglish ?? ''
                    )
                  : Parser().parse(
                      reservation?.eventTypeDescriptionInGerman ?? ''
                    )}
              </Box>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
}
